import { useState, useEffect } from "react";
import NitroService from "../utils/nitro.service";
import {
  ISurroundingLocationsParams,
  IQuotesByDestinationOld,
  IOriginExtension,
} from "../types/ICompAnalysisQMD";
import { convertCompAnalysisQMDOld } from "../utils/convertCompAnalysisQMDOld";
import { useNitroStore } from "../store/Store";

export interface IQuotesResponse {
  surroundingQuotes: IQuotesByDestinationOld[];
  loading: boolean;
  error: Error | null;
}

export const useQuotesForSurroundingLocationsOld = (
  quoteParams: ISurroundingLocationsParams,
  refetchTrigger?: number,
): IQuotesResponse => {
  const [featureFlags] = useNitroStore((state) => [state.featureFlags]);
  const { manageRailRate } = featureFlags;
  const [quotesResponse, setQuotesResponse] = useState<IQuotesResponse>({
    surroundingQuotes: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    // Skip fetch if city is null (no search to perform)
    if (!quoteParams.citystate_id) {
      setQuotesResponse({
        surroundingQuotes: [],
        loading: false,
        error: null,
      });
      return;
    }

    setQuotesResponse((prevData) => ({
      ...prevData,
      loading: true,
      error: null,
    }));

    const fetchData = async () => {
      try {
        const quotes = await NitroService.getSurroundingLocationsOld(
          quoteParams,
          0,
          0,
        );

        // Fetch and append pin types for origins
        // TODO: we also get details for favorite origins with rail turned on. do we need to do both sections or are we unecessarily duplicating calls?
        const originIds = new Set<string>();

        quotes.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin?.id) {
              originIds.add(quote.origin.id);
            }
          });
        });

        const locationDetailsMap = new Map<string, any>();
        if (originIds.size > 0) {
          try {
            const detailsArray = await NitroService.getMultiLocationDetails({
              ids: Array.from(originIds),
            });
            if (detailsArray) {
              detailsArray.forEach((details) => {
                if (details?.id && details.pinType) {
                  locationDetailsMap.set(details.id, details);
                }
              });
            }
          } catch (error) {
            console.error("Error fetching multi-location details:", error);
          }
        }

        quotes.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin?.id && locationDetailsMap.has(quote.origin.id)) {
              const locationDetails = locationDetailsMap.get(quote.origin.id);
              if (locationDetails) {
                const origin = quote.origin as IOriginExtension;
                // eslint-disable-next-line no-param-reassign
                origin.pinType = locationDetails.pinType;
                // eslint-disable-next-line no-param-reassign
                origin.city = locationDetails.city;
                // eslint-disable-next-line no-param-reassign
                origin.details = locationDetails;
              }
            }
          });
        });

        const quotesResult = await convertCompAnalysisQMDOld(quotes);

        setQuotesResponse({
          // only allow rail quotes through when the manageRailRate flag is on
          surroundingQuotes: manageRailRate
            ? quotesResult
            : quotesResult.map((entry) => ({
                ...entry,
                quotes: entry.quotes.filter(
                  (quote) => quote.mode?.toUpperCase() === "TRUCK",
                ),
              })),
          loading: false,
          error: null,
        });
      } catch (err) {
        setQuotesResponse((d) => ({
          ...d,
          loading: false,
          error: err as Error,
        }));
      }
    };

    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteParams.citystate_id, quoteParams.party_id, refetchTrigger]);

  return quotesResponse;
};
